//#region imports
import * as angular from 'angular';
import { fn } from '@common/utils';
import { RemoveStyle } from '@common/style';
import { AsObservableArray, WrapAsObservable, IMarkupCustomization, IGridCustomization } from '@common/navkendo';
import { IPageProperty } from '@common/navdata';
import { SearchInputs } from '../navkendo/searchinput/searchinputs';
import { IObservableArray } from '@common/nav';
import { DialogService } from '../services/dialogservice';
import { KWFApplicationService } from '../KWFApplication';
//#endregion

/** Obj necessary to edit markup Container */
interface IEditMarkups {
    GetMarkups: () => IMarkupCustomization[] | PromiseLike<IMarkupCustomization[]>;
    /** Save Markups */
    SaveMarkups: (markups: IMarkupCustomization[]) => IMarkupCustomization[] | PromiseLike<IMarkupCustomization[]>;
    /** Reset to Default */
    ResetMarkups?: () => IMarkupCustomization[] | PromiseLike<IMarkupCustomization[]>;
    /** Selectable Properties */
    Properties: () => ArrayLike<IPageProperty> | PromiseLike<ArrayLike<IPageProperty>>;
    /** optional injector access */
    injector?: ng.auto.IInjectorService;
    /** Window Title */
    title?: string;
}

/** Internal Helper Interface */
interface IEditMarkupsScopeEx {
    Markups: IObservableArray<IMarkupCustomization>;
    Properties: IObservableArray<IPageProperty>;
    Save: (items: IMarkupCustomization[]) => IMarkupCustomization[] | PromiseLike<IMarkupCustomization[]>;
    Reset?: () => IMarkupCustomization[] | PromiseLike<IMarkupCustomization[]>;
}

/** Edit MarkupCustomizations */
function ModalEdit(config: IEditMarkups) {
    let injector = config.injector || <ng.auto.IInjectorService>angular.element(window.document).injector(),
        $c = injector && injector.get("CompatibilityService"),
        dlg = injector && injector.get("DialogService");
    if (!dlg)
        throw new Error("Failed to resolve 'DialogService'!");

    return $c.when(config.GetMarkups()).then(resolvedMarkups => {
        return $c.when(config.Properties()).then(resolvedProperties => {
            resolvedProperties = fn.ToArray(resolvedProperties).filter(property => property.Invisible !== true).sort((p1, p2) => p1.Title.localeCompare(p2.Title));
            return dlg.ShowWindow({
                windowOptions: {
                    actions: ["Maximize", "Close"],
                    resizable: true,
                    modal: true,
                    title: config.title || "Formatvorlagen für Belege",
                    minWidth: 852,
                    minHeight: 300,
                    height: 500,
                    scrollable: false
                },
                controller: ["$scope", function ($scope) {
                    fn.ExtendObject($scope, <IEditMarkupsScopeEx>{
                        Markups: AsObservableArray(resolvedMarkups),
                        Properties: AsObservableArray(resolvedProperties),
                        Save: (items) => config.SaveMarkups(items),
                        Reset: config.ResetMarkups
                    });
                }],
                template: `<nav-edit-markup-customizations><nav-edit-markup-customizations>`
            }).then(w => {
                w.scope.$on(CtrlEditMarkupCustomizations.Close, () => w.window.close());
                return w;
            });
        });
    });
}

export class GlobalMarkupService {
    static $inject = ["$injector", "KWFApplication"];
    constructor(private $injector: ng.auto.IInjectorService, private KWFApplication: KWFApplicationService) {
        // Enable Edit Global Markups for admins only
        KWFApplication.GetGlobalSettings().then(s => {
            this.EditAction.Enabled = s && s.User.IsAdmin;
        });
    }
    readonly EditAction = {
        Hidden: "Enabled",
        Enabled: false,

        /** Edit with optional explicit external injector (used from Tests) */
        Show: () => {
            var kwf = this.KWFApplication,
                rwfAppSettings = kwf.RwfAppSettings,
                getMarkups = () => rwfAppSettings.Get().then((appConfig) => fn.clone(appConfig.RowCustomizations || [])),
                saveMarkups = (items: IMarkupCustomization[]) => {
                    return rwfAppSettings.Get().then((currSettings) => {
                        var toStore = fn.clone(currSettings);
                        toStore.RowCustomizations = items;
                        return rwfAppSettings.Save(toStore).then(settings => settings.RowCustomizations);
                    });
                },
                getProperties = () => kwf.GetInvoiceData().then(data =>
                    data.MyInvoices.Fields.filter(f => !!f.Name).map(f => {
                        if (f && f.EnumMappings && f.IsUsers == true) {
                            // We Don't want User Enum Choice UI here (@ich, .... as magictokens must be available)
                            f = fn.clone(f);
                            delete f.EnumMappings;
                        }
                        return f;

                    }));

            return ModalEdit({
                GetMarkups: getMarkups,
                SaveMarkups: saveMarkups,
                Properties: getProperties,
                title: 'Formatvorlagen für Belege (gültig für alle Benutzer)',
                injector: this.$injector
            });
        },
        // Default Operation redirect to show with default injector
        Execute: () => this.EditAction.Show()
    };
}

/** Controller for edit Single MarkupCustomization */
class CtrlEditMarkupCustomization {

    static ApplyCurrent = "Markup.ApplyCurrent";

    static $inject = ["$scope"];

    constructor(private $scope: ng.IScope & {
        Markup: IMarkupCustomization,
        EditMarkups: CtrlEditMarkupCustomizations
    }) {

        var parent = $scope.EditMarkups;
        this.SearchInputs = new SearchInputs({
            AutoSelect: false,
            AvailableProperties: parent && parent.AvailableProperties,
            ShowOperators: true
        });

        $scope.$watch(() => $scope.Markup, m => this.SetCurrentMarkup(m));
        $scope.$on(CtrlEditMarkupCustomization.ApplyCurrent, () => this.ApplyCurrent());
    }

    /** Set Currently Displayed Markup */
    SetCurrentMarkup(markup: IMarkupCustomization) {
        // store Current before change
        this.ApplyCurrent();

        delete this.TextCondition;

        this.Markup = markup;

        let conditions = markup && markup.Condition;

        if (IsString(conditions)) {
            this.TextCondition = { Value: <string>conditions };
        } else {
            if (!IsFilters(conditions) || conditions.length == 0)
                conditions = [{}];
            if (IsFilters(conditions)) {
                this.SearchInputs.splice(0);
                conditions.forEach(item => {
                    let si = this.SearchInputs.AppendSearchInput(),
                        op = item.operator;
                    if (typeof op == "function")
                        throw new Error("Function as Operator not supported");
                    si.SetProperty(item.field, item.value, op);
                });
            }
        }
        function IsFilters(item: any): item is kendo.data.DataSourceFilterItem[] {
            return item && (typeof item != "string") && (item.length >= 0);
        }

        function IsString(c: any): c is string {
            return !!(typeof c == "string" && c);
        }
    }

    /** Apply Current UI State to Model */
    ApplyCurrent = () => {
        // Store previous Changes
        var current = this.Markup;
        if (current) {
            current.Condition = (this.TextCondition && this.TextCondition.Value) || this.SearchInputs.GetAsFilterItems();
        }
    }

    public Markup: IMarkupCustomization;

    /** Container of Search Inputs */
    public SearchInputs: SearchInputs;

    /** Do we want to be able to Edit Condition as Text Only also? */
    public TextCondition: { Value: string };

    /** Operation : Delete Item */
    Delete = {
        Execute: () => {
            this.$scope.EditMarkups.DeleteMarkup(this.Markup);
        },
        Enabled: true
    };

    Duplicate = {
        Execute: () => {
            this.$scope.EditMarkups.CloneMarkup(this.Markup);
        },
        Enabled: true
    };

    static Directive = [function (): ng.IDirective {
        return {
            scope: {
                "Markup": "=markup",
                "EditMarkups": "=editmarkups"
            },
            controller: CtrlEditMarkupCustomization,
            controllerAs: "vm",
            template: require('./editmarkup.html')
        };
    }];
}

/** Controller for Edit Markup Customization Array */
class CtrlEditMarkupCustomizations {
    static Close = "CtrlEditMarkupCustomizations.Close";
    static $inject = ["$scope", "$q", "DialogService"];

    constructor(private $scope: ng.IScope & IEditMarkupsScopeEx, private $q: ng.IQService, private DialogService: DialogService) {

        const waitInit = $scope.$watch(s => $scope.Markups && $scope.Properties, ((newVal) => {
            if (newVal) {
                waitInit();
                this._PersistedMarkus = fn.ToArray(($scope.Markups || [])).map(item => fn.clone(item));
                this.Markups = AsObservableArray($scope.Markups);
                this.AvailableProperties = AsObservableArray($scope.Properties);
                this.SelectedMarkup = this.Markups && this.Markups[0];
                this.RefreshState();
            }
        }));

        // D&D placeholder
        var dndPlaceholder = (element: JQuery) => {
            var clonedElement = element.clone().css({
                "opacity": 0.5,
                "border": "1px dashed #000000",
                "width": $(element).parent().innerWidth()
            });
            clonedElement.find(".idms_Details").remove();
            return clonedElement;
        };

        // Set Up Displayed List as Sortable
        var waitList = $scope.$watch(() => this.MarkupsList, (list) => {
            if (list) {
                waitList();
                list.element.kendoSortable({
                    filter: ">div",
                    ignore: "input",
                    hint: dndPlaceholder,
                    placeholder: dndPlaceholder,
                    //cursorOffset: { top: -15, left: -15 },
                    start: e => {
                        // MANST TODO
                        this.$scope.$broadcast(CtrlEditMarkupCustomization.ApplyCurrent);
                    },
                    end: e => {
                        var dataSource = list.dataSource,
                            newIndex = e.newIndex,
                            dataItem = dataSource.getByUid(e.item.data("uid"));

                        dataSource.remove(dataItem);
                        dataSource.insert(newIndex, dataItem);
                    }
                });
            }
        });

        this.RefreshState();
    }

    public AvailableProperties: IObservableArray<IPageProperty>;
    /** Current Container of Markups */
    public Markups: IObservableArray<IMarkupCustomization>;
    /** Clone of Current Persited State (for late checking of pending changes) */
    private _PersistedMarkus: IMarkupCustomization[];
    /** Currently Selected Markup Item */
    public SelectedMarkup: IMarkupCustomization;
    /** Control that displays the List of Markups on UI */
    public MarkupsList: kendo.ui.ListView;

    public SetCurrentMarkup(m: IMarkupCustomization) {
        this.$scope.$broadcast(CtrlEditMarkupCustomization.ApplyCurrent);
        this.SelectedMarkup = m;
        this.RefreshState();
    }

    public DeleteMarkup(toDelete: IMarkupCustomization) {
        var markups = this.Markups,
            idx = markups.indexOf(toDelete);
        this.SetCurrentMarkup(markups[idx == 0 ? idx + 1 : idx - 1]);
        markups.remove(toDelete);
    }

    public CloneMarkup(toClone: IMarkupCustomization) {
        var idx = this.Markups.indexOf(toClone);
        if (idx < 0)
            return;

        this.SetCurrentMarkup(undefined);

        var result = fn.clone(toClone);
        result.Id = fn.id();
        result.Disabled = true;
        result.Description = "Kopie von " + toClone.Description;

        this.Markups.splice(idx + 1, 0, result)[0];
        // imp "conversion" to Observable Array ....
        var result = fn.FindFirst(this.Markups, m => m.Id == result.Id);

        this.SetCurrentMarkup(result);
    }

    /** refresh Container of markups */
    private refreshMarkups(items: IMarkupCustomization[]) {
        items = items || [];
        if (!Array.isArray(items))
            return false;

        this._PersistedMarkus.forEach(persistedItem => {
            var isDeleted = !fn.FindFirst(items, item => item.Id == persistedItem.Id);
            if (isDeleted) {
                RemoveStyle(persistedItem);
            }
        });

        this._PersistedMarkus = fn.clone(items);
        var m = this.Markups;
        m.splice(0, m.length, ...items);
        return true;
    }

    /** Operation : New Item */
    New = {
        Execute: () => {
            // Wrap : Ensure newMarkup equals item in Observable Array
            var newMarkup = WrapAsObservable(<IMarkupCustomization>{
                Id: fn.id(),
                Disabled: false,
                Condition: [{ field: "", operator: "eq", value: "" }],
                BackColor: "#ffffff", // TODO: Automatically offer next standard color
                TextColor: "#000000",
                Description: "Neue Stilregel"
            });

            // Caution : Markups.push will cleanup / create kendo angular on List-View ... wOut Resetting current beforeHand --> error in kendo
            // MANST : my Opinion : bug in kendo
            this.SetCurrentMarkup(undefined);

            this.Markups.push(newMarkup);
            this.SetCurrentMarkup(newMarkup);
        },
        Enabled: false,
    };
    /** Operation : Save Items */
    Save = {
        Execute: () => {
            return this.DialogService.WhenConfirm("Änderungen wirklich speichern?").then(() => {
                var markups = this.Markups,
                    prevIdx = markups.indexOf(this.SelectedMarkup);
                this.SetCurrentMarkup(undefined);
                return this.$q.when(this.$scope.Save(fn.ToArray(this.Markups))).then(items => {
                    this.$scope.$root.$broadcast("$KWF.MarkupsChanged", 
                        <IGridCustomization> {RowCustomizations : items } );
                    if (this.refreshMarkups(items)) {
                        this.SetCurrentMarkup(markups[prevIdx] || markups[0]);
                    }
                });
            });
        },
        Enabled: true
    };

    /** 'Reset' Markup Operation */
    Reset = {
        Execute: () => {
            return this.DialogService.WhenConfirm("Formatvorlagen auf den Standard zurücksetzen?").then(() =>
                this.$scope.Reset()).then(items => {
                    this.SetCurrentMarkup(undefined);
                    this.refreshMarkups(items);
                });
        },
        Hidden: "Enabled",
        Enabled: false
    };

    /** Close UI Operation with Save Pending Changes if any */
    Close = {
        Execute: () => {
            this.SetCurrentMarkup(undefined);
            var curr = this.Markups.toJSON(),
                hasChanges = this._PersistedMarkus && JSON.stringify(this._PersistedMarkus) !== JSON.stringify(curr);

            var result = this.$q.when(<any>undefined);

            if (hasChanges) {
                result = result.then(() =>
                    this.DialogService.ShowConfirm("Änderungen speichern?").then(ok =>
                        ok && this.$scope.Save(fn.ToArray(this.Markups))));
            }

            // Request Close after Save
            return result.then(() => {
                this.$scope.$emit(CtrlEditMarkupCustomizations.Close);
            });
        }
    };

    /** Refresh Current (actions) State */
    RefreshState = () => {
        this.New.Enabled = !!this.Markups;
        this.Reset.Enabled = !!this.$scope.Reset;
        this.Save.Enabled = !!this.$scope.Save;
    }

    static Directive = [function (): ng.IDirective {
        return {
            controller: CtrlEditMarkupCustomizations,
            controllerAs: "vm",
            template: require('./editmarkups.html'),
        };
    }];
}

angular.module("infoma.editmarkup", ["infoma.angularEx"])
    .controller("CtrlEditMarkupCustomizations", CtrlEditMarkupCustomizations)
    .service("GlobalMarkup", GlobalMarkupService)
    .directive("navEditMarkupCustomization", CtrlEditMarkupCustomization.Directive)
    .directive("navEditMarkupCustomizations", CtrlEditMarkupCustomizations.Directive);
